import { Article, ArticleResolution, ArticleType } from '@wix/answers-api';
import { RenderHTML } from '@wix/cc-user-facing-common-components';
import { Box } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import classNames from 'classnames';
import React, {
  FunctionComponent,
  useContext,
} from 'react';

import { HirePartnerBanner } from './HirePartnerBanner';
import { RelatedContent } from './RelatedContent';
import { StudioBanner } from './StudioBanner';
import { Context } from '../../../context';
import useIsHelpCenterCopilotEnabled from '../../../hooks/useIsHelpCenterCopilotEnabled';
import useIsHelpmateImprovedFeedbackEnabled from '../../../hooks/useIsHelpmateImprovedFeedbackEnabled';
import { ArticleSearchResultItem, PageType } from '../../../types';
import { mapArticleTypeToItemType } from '../../../utils/bi';
import {
  isTwinsRelated,
  isWixelRelated,
} from '../../../utils/excluded-categories';
import { useStore } from '../../../zustand/stores';
import { ArticleFeedback } from '../../ArticleFeedback';
import { RelatedArticles } from '../../RelatedArticles';
import { Vote } from '../../Vote';

import css from './index.module.scss';

export type ArticleFooterProps = {
  article: ArticleSearchResultItem;
  isRoadmapArticle?: boolean;
  hasNoContent?: boolean;
  relatedArticles: Article[];
  selfArticleRedirect: (
    url: string,
    shallow?: boolean,
    targetLocale?: string
  ) => void;
};

export const ArticleFooter: FunctionComponent<ArticleFooterProps> = ({
  article,
  isRoadmapArticle,
  hasNoContent,
  relatedArticles,
  selfArticleRedirect,
}: ArticleFooterProps) => {
  const { t } = useTranslation();
  const isHelpmateImprovedFeedbackEnabled =
    useIsHelpmateImprovedFeedbackEnabled();
  const categoriesTree = useStore(
    (state) => state.categoriesTreeSlice.categoriesTree
  );
  const { user } = useStore((state) => state.userSlice);
  const { isWixStudioUser } = useStore((state) => state.userSlice);
  const { pageTemplateData } = useContext(Context);
  const isHelpCenterCopilotEnabled = useIsHelpCenterCopilotEnabled();

  const isWixelArticle = isWixelRelated(
    PageType.Article,
    categoriesTree,
    pageTemplateData
  );
  const isTwinsArticle = isTwinsRelated(
    PageType.Article,
    categoriesTree,
    pageTemplateData
  );


  const shouldHideRelatedArticles = isHelpCenterCopilotEnabled && !user;

  return (
    <Box
      direction="vertical"
      className={classNames(css.articleFooter, css.articleFooterNew, {
        [css.roadmapArticleFooter]: isRoadmapArticle,
        [css.hasNoContent]: hasNoContent,
        [css.articleFooterCopilot]: isHelpCenterCopilotEnabled,
      })}
    >
      {article.type !== ArticleType.ARTICLE &&
        (article.resolution === ArticleResolution.RELEASED ? null : (
          <Box direction="vertical">
            <Vote
              article={article}
              hasNoContent={hasNoContent}
            />
          </Box>
        ))}
      {article.type !== ArticleType.KNOWN_ISSUE && (
        <Box direction="vertical" gap="SP5">
          {article.type === ArticleType.ARTICLE && (
            <Box
              className={classNames(css.articleFeedback, {
                [css.articleFeedbackCopilot]: isHelpCenterCopilotEnabled,
              })}
            >
              {!isHelpmateImprovedFeedbackEnabled && (
                <ArticleFeedback
                  articleId={article.id}
                  categoryId={article.categoryId}
                  key={article.id}
                />
              )}
            </Box>
          )}
          {relatedArticles.length > 0 && !shouldHideRelatedArticles && (
            <>
              <RelatedContent
                relatedArticles={relatedArticles}
                selfArticleRedirect={selfArticleRedirect}
              />
              <Box className={css.relatedArticles}>
                <RelatedArticles
                  itemId={article.id}
                  itemType={mapArticleTypeToItemType(article.type)}
                  relatedArticles={relatedArticles}
                  pageType={PageType.Article}
                  selfArticleRedirect={selfArticleRedirect}
                />
              </Box>
            </>
          )}
          {!isWixelArticle &&
            !isTwinsArticle &&
            (isWixStudioUser ? (
              <StudioBanner articleId={article.id} articleType={article.type} />
            ) : (
              <HirePartnerBanner
                articleId={article.id}
                categoryId={article.categoryId}
              />
            ))}
          {isRoadmapArticle && (
            <RenderHTML
              textElement={{
                size: 'tiny',
                weight: 'thin',
                className: css.featureRequestNoteMark,
              }}
              html={t('article-footer.feature-request.note-mark')}
            />
          )}
        </Box>
      )}
    </Box>
  );
};
